import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRedditAuth } from '../RedditAuthContext';
import redditApi from '../../../services/redditApi';
import { useAccountQualityCheck, AccountQualityResult } from '../components/AccountQualityCheck';
import ThreadHealth from '../components/ThreadHealth';
import '../reddit.css';

const REDDIT_COMMENT_RE = /^https?:\/\/(www\.)?reddit\.com\/r\/[^/]+\/comments\/[a-z0-9]+\/(comment|[^/]+)\/[a-z0-9]+/i;

function toAbsoluteRedditUrl(url) {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://www.reddit.com${url.startsWith('/') ? '' : '/'}${url}`;
}

function Toast({ message, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className={`rh-toast rh-toast-${type}`}>{message}</div>;
}

function StatusBadge({ comment }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(comment.publishing_date);
  const isOverdue = comment.status === 'scheduled' && due < today;
  const isToday = comment.status === 'scheduled' && due.toDateString() === today.toDateString();

  if (comment.status === 'submitted') return <span className="rh-badge rh-badge-submitted">Submitted</span>;
  if (comment.status === 'live') return <span className="rh-badge rh-badge-live">Live</span>;
  if (comment.status === 'deleted') return <span className="rh-badge rh-badge-deleted">Deleted</span>;
  if (comment.status === 'dws') return <span className="rh-badge rh-badge-dws">Instant Deletion</span>;
  if (comment.status === 'skipped') return <span className="rh-badge rh-badge-skipped">Skipped</span>;
  if (isOverdue) return <span className="rh-badge rh-badge-overdue">Overdue</span>;
  if (isToday) return <span className="rh-badge rh-badge-today">Due Today</span>;
  return <span className="rh-badge rh-badge-scheduled">Scheduled</span>;
}

function isOverdueComment(comment, today) {
  return comment.status === 'scheduled' && new Date(comment.publishing_date) < today;
}

function TodoAssignmentRow({
  comment: c,
  today,
  hasOverdues,
  onView,
  onCopy,
  onSubmit,
  onSkip,
}) {
  const due = new Date(c.publishing_date);
  const isOverdue = c.status === 'scheduled' && due < today;
  const isToday = c.status === 'scheduled' && due.toDateString() === today.toDateString();
  const dueFmt = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const monthFmt = due.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const postDateFmt = c.post_date
    ? new Date(c.post_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
  const truncated = c.comment_text.length > 80;
  const preview = truncated ? c.comment_text.slice(0, 80) + '…' : c.comment_text;

  const dueMonthKey = due.getFullYear() * 12 + due.getMonth();
  const nowMonthKey = today.getFullYear() * 12 + today.getMonth();
  const isFutureMonth = c.status === 'scheduled' && dueMonthKey > nowMonthKey;
  const canSubmit = c.status === 'scheduled' && !isFutureMonth;
  const waitForOverdue = hasOverdues && !isOverdue && canSubmit;

  return (
    <tr
      className={isOverdue ? 'row-overdue' : ''}
      style={isFutureMonth || waitForOverdue ? { opacity: isFutureMonth ? 0.55 : 0.72 } : undefined}
    >
      <td className="rh-font-medium" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {c.client_name}
      </td>
      <td style={{ overflow: 'hidden' }}>
        <a className="rh-link" href={toAbsoluteRedditUrl(c.reddit_url)} target="_blank" rel="noreferrer"
          style={{ fontSize: 13, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {c.post_title}
        </a>
      </td>
      <td className="rh-col-center" style={{ fontSize: 13, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>
        {postDateFmt}
      </td>
      <td style={{ verticalAlign: 'top', paddingTop: 12 }}>
        {isFutureMonth ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text-faint)', fontStyle: 'italic', lineHeight: 1.6 }}>
            Available in {monthFmt}
          </p>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text)', lineHeight: 1.6, wordBreak: 'break-word' }}>
              {preview}
              {truncated && (
                <>
                  {' '}
                  <button
                    style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: 12, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                    onClick={() => onView(c)}
                  >
                    View full
                  </button>
                </>
              )}
            </p>
            <button
              style={{ marginTop: 5, background: '#fff', border: '1px solid var(--rh-border)', borderRadius: 6, padding: '2px 10px', fontSize: 11, color: 'var(--rh-text-muted)', cursor: 'pointer', fontFamily: 'inherit' }}
              onClick={() => onCopy(c.comment_text)}
            >
              Copy
            </button>
          </>
        )}
      </td>
      <td className="rh-col-center" style={{ whiteSpace: 'nowrap' }}>
        <StatusBadge comment={c} />
        <ThreadHealth comment={c} />
      </td>
      <td
        className="rh-col-center"
        style={{
          fontSize: 13, whiteSpace: 'nowrap',
          color: isOverdue ? '#c2410c' : isToday ? '#92400e' : 'var(--rh-text-muted)',
          fontWeight: (isOverdue || isToday) ? 600 : undefined,
        }}
      >
        {dueFmt}
      </td>
      <td style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {c.recommended_account_username ? (
          <span
            style={{ fontFamily: 'monospace', fontWeight: 600 }}
            title={c.recommended_account_tier ? `u/${c.recommended_account_username} · Niche fit: ${c.recommended_account_tier}` : `u/${c.recommended_account_username}`}
          >
            u/{c.recommended_account_username}
          </span>
        ) : (
          <span style={{ color: 'var(--rh-text-faint)' }}>—</span>
        )}
      </td>
      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
        {canSubmit ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <button
              className={waitForOverdue ? 'rh-btn-secondary' : 'rh-btn-primary'}
              style={{ fontSize: 12, padding: '7px 12px', whiteSpace: 'nowrap' }}
              onClick={() => onSubmit(c)}
            >
              {isOverdue ? 'Post now' : 'Mark as Submitted'}
            </button>
            {waitForOverdue && (
              <span style={{ fontSize: 10, color: '#9a3412', lineHeight: 1.3, maxWidth: 140 }}>
                Overdue comments first
              </span>
            )}
            <button
              type="button"
              className="rh-btn-secondary"
              style={{ fontSize: 11, padding: '5px 10px', whiteSpace: 'nowrap' }}
              onClick={() => onSkip(c)}
              title="Skip this comment if you cannot post it"
            >
              Skip Comment
            </button>
          </div>
        ) : isFutureMonth ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 12, color: 'var(--rh-text-faint)',
            background: 'var(--rh-bg)', border: '1px solid var(--rh-border)',
            borderRadius: 6, padding: '5px 10px',
            whiteSpace: 'normal', lineHeight: 1.4,
          }}>
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Unlocks {monthFmt}
          </span>
        ) : (c.status === 'submitted' || c.status === 'live') ? (
          <a className="rh-link" href={c.submitted_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            View on Reddit ↗
          </a>
        ) : (
          <span style={{ color: 'var(--rh-text-faint)', fontSize: 12 }}>—</span>
        )}
      </td>
    </tr>
  );
}

function ViewCommentModal({ comment, onClose }) {
  function copy() {
    navigator.clipboard.writeText(comment.comment_text);
  }
  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <h3 className="rh-modal-title">Comment Details</h3>
          <button className="rh-modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          {[
            ['Client', comment.client_name],
            ['Subreddit', comment.subreddit ? (comment.subreddit.startsWith('r/') ? comment.subreddit : `r/${comment.subreddit}`) : '—'],
            ['Comment Date', comment.publishing_date],
            ['Status', null],
          ].map(([label, val]) => (
            <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
              {label === 'Status'
                ? <div style={{ marginTop: 4 }}><StatusBadge comment={comment} /></div>
                : <p style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 500 }}>{val}</p>
              }
            </div>
          ))}
        </div>
        <div style={{ background: '#f9fafb', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--rh-text-muted)' }}>Full Comment</p>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.7, color: 'var(--rh-text)', whiteSpace: 'pre-wrap' }}>{comment.comment_text}</p>
        </div>
        {comment.submitted_url && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--rh-text-muted)' }}>Published URL</p>
            <a className="rh-link" href={comment.submitted_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, wordBreak: 'break-all' }}>
              {comment.submitted_url}
            </a>
          </div>
        )}
        {comment.redditor_note && (
          <div style={{ marginBottom: 16, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px' }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#92400e' }}>Remarks</p>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: '#78350f', whiteSpace: 'pre-wrap' }}>{comment.redditor_note}</p>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="rh-btn-secondary" style={{ flex: 1 }} onClick={copy}>Copy Comment</button>
          <button className="rh-btn-secondary" style={{ flex: 1 }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function cleanRedditUrl(raw) {
  try {
    const u = new URL(raw.trim());
    // Strip query string, UTM params, and fragment — keep only scheme + host + path
    const path = u.pathname.replace(/\/?$/, '/');  // ensure trailing slash
    return `${u.origin}${path}`;
  } catch {
    return raw.trim();
  }
}

function SubmitModal({ comment, onClose, onSubmitted, onManageAccounts }) {
  const [deletedByReddit, setDeletedByReddit] = useState(false);
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const [accountDropOpen, setAccountDropOpen] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountChoiceReason, setAccountChoiceReason] = useState('');
  const [accountChoiceReasonError, setAccountChoiceReasonError] = useState('');
  const accountDropRef = React.useRef(null);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotError, setScreenshotError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);
  const [note, setNote] = useState(comment.redditor_note || '');
  const [noteError, setNoteError] = useState('');
  const [busy, setBusy] = useState(false);

  const recommendedId = comment.recommended_account_id || '';
  const recommendedUsername = comment.recommended_account_username || '';
  const divergingFromRecommended = Boolean(
    recommendedId && selectedAccount && selectedAccount !== recommendedId,
  );

  useEffect(() => {
    redditApi.get('/my/accounts/').then(({ data }) => {
      // Only quality-passed (or admin force-added) accounts can be used for submissions.
      const usable = (data || []).filter((a) => a.validation_status === 'passed' || a.force_added);
      setAccounts(usable);
      if (recommendedId && usable.some((a) => String(a.id) === String(recommendedId))) {
        setSelectedAccount(String(recommendedId));
      } else if (usable.length === 1) {
        setSelectedAccount(usable[0].id);
      }
    }).catch(() => {});
  }, [recommendedId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (accountDropRef.current && !accountDropRef.current.contains(e.target)) {
        setAccountDropOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleUrlChange(raw) {
    setUrlError('');
    setUrl(cleanRedditUrl(raw));
  }

  function handleScreenshot(file) {
    setScreenshotError('');
    if (!file) { setScreenshot(null); return; }
    const allowed = ['image/png', 'image/jpeg'];
    if (!allowed.includes(file.type)) {
      setScreenshotError('Only PNG and JPG/JPEG files are accepted. Please convert your screenshot and try again.');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setScreenshotError('Screenshot exceeds 2 MB. Please resize the image (e.g. using Preview on Mac or Paint on Windows) to reduce the file size, then upload it again.');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setScreenshot(file);
  }

  async function onConfirm() {
    setUrlError('');
    setScreenshotError('');
    setAccountError('');
    setNoteError('');
    setAccountChoiceReasonError('');

    if (!selectedAccount) {
      setAccountError('Please select the Reddit account you used.');
      return;
    }

    if (divergingFromRecommended && !accountChoiceReason.trim()) {
      setAccountChoiceReasonError(
        `Please explain why you are not using the recommended account u/${recommendedUsername}.`,
      );
      return;
    }

    const cleanUrl = url.trim() ? cleanRedditUrl(url) : '';

    if (!deletedByReddit) {
      if (!REDDIT_COMMENT_RE.test(cleanUrl)) {
        setUrlError('Please enter a valid Reddit comment URL (e.g. https://www.reddit.com/r/sub/comments/abc/comment/xyz/).');
        return;
      }
      if (!screenshot) {
        setScreenshotError('Please upload a screenshot confirming the comment was posted.');
        return;
      }
    } else if (cleanUrl && !REDDIT_COMMENT_RE.test(cleanUrl)) {
      // DWS: URL is optional, but if provided it must be valid
      setUrlError('Must be a valid Reddit comment URL, or leave blank.');
      return;
    }

    if (deletedByReddit && !note.trim()) {
      setNoteError('Please add remarks explaining what happened.');
      return;
    }

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('deleted_while_submitting', deletedByReddit ? 'true' : 'false');
      formData.append('reddit_account_id', selectedAccount);
      if (note.trim()) formData.append('redditor_note', note.trim());
      if (divergingFromRecommended && accountChoiceReason.trim()) {
        formData.append('account_choice_reason', accountChoiceReason.trim());
      }
      if (cleanUrl) formData.append('submitted_url', cleanUrl);
      if (screenshot) formData.append('screenshot', screenshot);
      await redditApi.post(`/my/comments/${comment.id}/submit/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSubmitted(deletedByReddit);
      onClose();
    } catch (err) {
      const d = err.response?.data;
      const choiceErr = d?.account_choice_reason?.[0] || '';
      if (choiceErr) setAccountChoiceReasonError(choiceErr);
      setUrlError(d?.submitted_url?.[0] || (!choiceErr ? (d?.detail || 'Failed to submit. Try again.') : ''));
      if (d?.detail && choiceErr) setAccountError(d.detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal">
        <h3 className="rh-modal-title">Mark as Submitted</h3>
        <p className="rh-modal-subtitle">
          {deletedByReddit
            ? 'Record that this comment was deleted by Reddit immediately after posting.'
            : 'Paste the Reddit comment URL to confirm this has been posted.'}
        </p>

        <div className="rh-modal-preview">
          <p style={{ margin: 0, lineHeight: 1.7 }}>{comment.comment_text}</p>
          <div className="rh-modal-meta">
            <span>{comment.subreddit ? (comment.subreddit.startsWith('r/') ? comment.subreddit : `r/${comment.subreddit}`) : '—'}</span>
            <span>·</span>
            <span style={{ fontWeight: 500 }}>{comment.client_name}</span>
          </div>
        </div>

        {/* Deleted by Reddit toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: deletedByReddit ? '#fff7ed' : 'var(--rh-bg)',
          border: `1.5px solid ${deletedByReddit ? '#f97316' : 'var(--rh-border)'}`,
          borderRadius: 8, padding: '8px 12px', marginBottom: 12, cursor: 'pointer',
          transition: 'background 0.15s, border-color 0.15s',
        }} onClick={() => { setDeletedByReddit((v) => !v); setUrlError(''); setScreenshotError(''); setNoteError(''); }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: deletedByReddit ? '#c2410c' : 'var(--rh-text)' }}>
              Instant Deletion
            </div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>
              Turn on if Reddit removed this comment immediately after posting
            </div>
          </div>
          <div style={{
            width: 36, height: 20, borderRadius: 10, position: 'relative', flexShrink: 0,
            background: deletedByReddit ? '#f97316' : '#d1d5db',
            transition: 'background 0.2s',
          }}>
            <div style={{
              position: 'absolute', top: 2, left: deletedByReddit ? 18 : 2,
              width: 16, height: 16, borderRadius: '50%', background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
            }} />
          </div>
        </div>

        <div className="rh-form-group">
          <label>
            Reddit Comment URL{' '}
            {deletedByReddit
              ? <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', fontWeight: 400 }}>(optional)</span>
              : <span style={{ color: '#dc2626' }}>*</span>}
          </label>
          <input
            type="url"
            placeholder="https://www.reddit.com/r/subreddit/comments/.../comment/..."
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            onPaste={(e) => {
              e.preventDefault();
              handleUrlChange(e.clipboardData.getData('text'));
            }}
            autoFocus={!deletedByReddit}
            style={deletedByReddit ? { opacity: 0.7 } : undefined}
          />
          {urlError && <p className="rh-input-error">{urlError}</p>}
        </div>
        <div className="rh-form-group">
          <label>Reddit Account Used <span style={{ color: '#dc2626' }}>*</span></label>
          <div ref={accountDropRef} style={{ position: 'relative' }}>
            {/* Trigger button */}
            <button
              type="button"
              onClick={() => { setAccountDropOpen((v) => !v); setAccountSearch(''); }}
              style={{
                width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: '#fff', border: '1.5px solid var(--rh-border)', borderRadius: 8,
                padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: selectedAccount ? 'var(--rh-text)' : 'var(--rh-text-muted)',
                ...(accountDropOpen ? { borderColor: 'var(--rh-orange)', boxShadow: '0 0 0 3px rgba(255,69,0,0.12)' } : {}),
              }}
            >
              <span style={{ fontFamily: selectedAccount ? 'monospace' : 'inherit', fontWeight: selectedAccount ? 600 : 400 }}>
                {selectedAccount
                  ? `u/${accounts.find((a) => a.id === selectedAccount)?.username || ''}`
                  : '— Select account —'}
              </span>
              <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                style={{ flexShrink: 0, transform: accountDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {/* Dropdown */}
            {accountDropOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
                background: '#fff', border: '1px solid var(--rh-border)', borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
              }}>
                {/* Search input */}
                <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--rh-border)' }}>
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search your accounts…"
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    style={{ width: '100%', border: '1px solid var(--rh-border)', borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace' }}
                  />
                </div>

                {/* Existing accounts filtered */}
                <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {accounts
                    .filter((a) => !accountSearch || a.username.toLowerCase().includes(accountSearch.replace(/^u\//i, '').toLowerCase()))
                    .map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => { setSelectedAccount(a.id); setAccountDropOpen(false); setAccountSearch(''); setAccountError(''); setAccountChoiceReasonError(''); }}
                        style={{
                          width: '100%', textAlign: 'left', background: selectedAccount === a.id ? 'var(--rh-orange-light)' : 'transparent',
                          border: 'none', padding: '9px 14px', fontSize: 13, fontFamily: 'monospace', fontWeight: 600,
                          color: selectedAccount === a.id ? 'var(--rh-orange)' : 'var(--rh-text)',
                          cursor: 'pointer', borderTop: '1px solid var(--rh-border)',
                        }}
                      >
                        u/{a.username}
                        {String(a.id) === String(recommendedId) && (
                          <span style={{ marginLeft: 8, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, color: 'var(--rh-orange)' }}>
                            Recommended
                          </span>
                        )}
                        {selectedAccount === a.id && (
                          <svg style={{ marginLeft: 6, verticalAlign: 'middle' }} width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </button>
                    ))
                  }

                  {/* No matching account — guide to My Accounts */}
                  {accountSearch.trim().replace(/^u\//i, '') &&
                    !accounts.some((a) => a.username.toLowerCase().includes(accountSearch.trim().replace(/^u\//i, '').toLowerCase())) && (
                    <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--rh-text-muted)', borderTop: '1px solid var(--rh-border)' }}>
                      Account not listed?{' '}
                      <button
                        type="button"
                        onClick={() => { setAccountDropOpen(false); onManageAccounts && onManageAccounts(); }}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--rh-orange)', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
                      >
                        Add it in My Accounts.
                      </button>
                    </div>
                  )}

                  {accounts.length === 0 && !accountSearch && (
                    <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--rh-text-muted)' }}>
                      No accounts yet.{' '}
                      <button
                        type="button"
                        onClick={() => { setAccountDropOpen(false); onManageAccounts && onManageAccounts(); }}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--rh-orange)', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
                      >
                        Add one in My Accounts.
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {accountError && <p className="rh-input-error">{accountError}</p>}
          {recommendedUsername && selectedAccount === recommendedId && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--rh-text-muted)' }}>
              Using recommended account for this client: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>u/{recommendedUsername}</span>
            </p>
          )}
        </div>
        {divergingFromRecommended && (
          <div className="rh-form-group">
            <label>
              Why not the recommended account (u/{recommendedUsername})?{' '}
              <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              className="rh-input"
              rows={2}
              value={accountChoiceReason}
              onChange={(e) => { setAccountChoiceReason(e.target.value); setAccountChoiceReasonError(''); }}
              placeholder="Briefly explain why you used a different account…"
              style={{ resize: 'vertical', minHeight: 56 }}
            />
            {accountChoiceReasonError && <p className="rh-input-error">{accountChoiceReasonError}</p>}
          </div>
        )}
        <div className="rh-form-group">
          <label>
            Screenshot{' '}
            {deletedByReddit
              ? <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', fontWeight: 400 }}>(optional)</span>
              : <span style={{ color: '#dc2626' }}>*</span>}
          </label>
          {/* Hidden real file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(e) => handleScreenshot(e.target.files[0] || null)}
          />
          {/* Drag-and-drop zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files[0];
              if (file) handleScreenshot(file);
            }}
            style={{
              border: `2px dashed ${dragOver ? 'var(--rh-orange)' : screenshot ? '#16a34a' : 'var(--rh-border)'}`,
              borderRadius: 8,
              padding: '14px 12px',
              textAlign: 'center',
              cursor: 'pointer',
              opacity: deletedByReddit && !screenshot ? 0.7 : 1,
              background: dragOver ? 'var(--rh-orange-light)' : screenshot ? '#f0fdf4' : 'var(--rh-bg)',
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            {screenshot ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <svg width="18" height="18" fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a' }}>{screenshot.name}</span>
                <span style={{ fontSize: 11, color: 'var(--rh-text-muted)' }}>
                  {(screenshot.size / 1024).toFixed(0)} KB · Click to replace
                </span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <svg width="20" height="20" fill="none" stroke="var(--rh-text-muted)" strokeWidth="1.8" viewBox="0 0 24 24">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--rh-text)' }}>
                  {dragOver ? 'Drop to upload' : 'Drag & drop or click to upload'}
                </span>
                <span style={{ fontSize: 11, color: 'var(--rh-text-muted)' }}>PNG or JPG/JPEG only · Max 2 MB</span>
              </div>
            )}
          </div>
          {screenshotError && <p className="rh-input-error">{screenshotError}</p>}
        </div>

        {/* Remarks — always visible; required only for Instant Deletion */}
        <div className="rh-form-group">
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>
              {deletedByReddit ? (
                <>Remarks <span style={{ color: '#dc2626' }}>*</span></>
              ) : (
                <>Remarks <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', fontWeight: 400 }}>(optional)</span></>
              )}
            </span>
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--rh-text-muted)' }}>
              {deletedByReddit
                ? 'Tell us what happened on Reddit (required for Instant Deletion).'
                : 'Share feedback — e.g. subreddit restrictions, Bot Bouncer, or if the comment felt too promotional.'}
            </span>
          </label>
          <textarea
            placeholder={deletedByReddit
              ? 'e.g. Posted successfully but comment disappeared suddenly and I could not capture the URL or screenshot'
              : 'e.g. Subreddit requires karma / Bot Bouncer flagged account / comment needed to sound less promotional'}
            value={note}
            onChange={(e) => { setNote(e.target.value); if (e.target.value.trim()) setNoteError(''); }}
            rows={3}
            style={{
              width: '100%', resize: 'vertical', borderRadius: 8, padding: '8px 10px',
              border: `1.5px solid ${noteError ? '#dc2626' : deletedByReddit ? '#f97316' : 'var(--rh-border)'}`,
              fontSize: 12, fontFamily: 'inherit',
              boxSizing: 'border-box', outline: 'none', lineHeight: 1.5,
              background: deletedByReddit ? '#fffbeb' : '#fff',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--rh-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,69,0,0.12)'; }}
            onBlur={(e) => {
              e.target.style.borderColor = noteError ? '#dc2626' : deletedByReddit ? '#f97316' : 'var(--rh-border)';
              e.target.style.boxShadow = 'none';
            }}
          />
          {noteError && <p className="rh-input-error">{noteError}</p>}
        </div>

        <div className="rh-modal-actions">
          <button className="rh-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="rh-btn-primary"
            style={{ background: deletedByReddit ? '#f97316' : '#16a34a' }}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Submitting…' : deletedByReddit ? 'Record as Instant Deletion' : 'Confirm Submitted'}
          </button>
        </div>
      </div>
    </div>
  );
}

const METHOD_LABELS = {
  paypal: 'PayPal', venmo: 'Venmo', wise: 'Wise', bank_transfer: 'Bank Transfer', other: 'Other',
};

function periodLabel(period) {
  if (!period) return '';
  const parts = period.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const half = parts[2]; // 'H1' | 'H2' | undefined
  const monthLong = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  if (half === 'H1') {
    const monthShort = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' });
    return `${monthShort} 1–15, ${y} (first half)`;
  }
  if (half === 'H2') {
    const monthShort = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' });
    const last = new Date(y, m, 0).getDate();
    return `${monthShort} 16–${last}, ${y} (second half)`;
  }
  return monthLong;
}

function MyPaymentsView({ onBack, data, loading }) {
  const kpis = data?.kpis || {};
  const pending = data?.pending_rows || [];
  const history = data?.history || [];

  return (
    <div className="rh-redditor-body">
      <button
        onClick={onBack}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--rh-text-muted)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16, padding: 0 }}
      >
        ← Back to Dashboard
      </button>
      <h2 className="rh-page-title" style={{ marginBottom: 4 }}>My Payments</h2>
      <p className="rh-page-subtitle" style={{ marginBottom: 20 }}>
        Paid twice a month (1–15 and 16–end) for comments posted in each period
        {data?.rate_per_comment > 0 && ` · $${parseFloat(data.rate_per_comment).toFixed(2)} per submitted comment`}
      </p>

      <div className="rh-kpi-row rh-kpi-row-4">
        <div className="rh-kpi-card rh-kpi-card--default">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value default">{loading ? '—' : `$${(kpis.total_earned || 0).toFixed(2)}`}</p>
            <p className="rh-kpi-label">Total Earned</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--default">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
        </div>
        <div className="rh-kpi-card rh-kpi-card--green">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value green">{loading ? '—' : `$${(kpis.total_paid || 0).toFixed(2)}`}</p>
            <p className="rh-kpi-label">Total Paid</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--green">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
        </div>
        <div className="rh-kpi-card rh-kpi-card--orange">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value orange">{loading ? '—' : `$${(kpis.due_now || 0).toFixed(2)}`}</p>
            <p className="rh-kpi-label">Awaiting Payout</p>
            <p className="rh-kpi-hint">Closed periods, unpaid</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--orange">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </div>
        </div>
        <div className="rh-kpi-card rh-kpi-card--blue">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value blue">{loading ? '—' : `$${(kpis.current_month || 0).toFixed(2)}`}</p>
            <p className="rh-kpi-label">Current Period</p>
            <p className="rh-kpi-hint">Still accruing</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--blue">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Pending / upcoming */}
      <div className="rh-card">
        <div className="rh-card-header">
          <div>
            <h3 className="rh-card-title">Upcoming & Current</h3>
            <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', marginTop: 2 }}>Periods that haven't been paid yet.</p>
          </div>
        </div>
        <div className="rh-table-wrap">
          <table className="rh-table">
            <thead>
              <tr><th>Month</th><th>Submitted</th><th>Rate</th><th>Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="rh-empty">Loading…</td></tr>
              ) : pending.length === 0 ? (
                <tr><td colSpan={5} className="rh-empty">Nothing pending — submit comments to start earning this month.</td></tr>
              ) : pending.map((r) => (
                <tr key={`${r.period}`}>
                  <td className="rh-font-medium">{periodLabel(r.period)}</td>
                  <td>{r.submitted}</td>
                  <td className="rh-text-muted">${r.rate.toFixed(2)}</td>
                  <td className="rh-font-medium">${r.amount.toFixed(2)}</td>
                  <td>
                    {r.status === 'current'
                      ? <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#dbeafe', color: '#1e40af' }}>Current Period</span>
                      : r.status === 'future'
                      ? <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#3730a3' }}>Upcoming</span>
                      : <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#ffedd5', color: '#9a3412' }}>Awaiting Payout</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* History */}
      <div className="rh-card">
        <div className="rh-card-header"><h3 className="rh-card-title">Payment History</h3></div>
        <div className="rh-table-wrap">
          <table className="rh-table">
            <thead>
              <tr><th>Date</th><th>Period</th><th>Comments</th><th>Amount</th><th>Method</th><th>Note</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="rh-empty">Loading…</td></tr>
              ) : history.length === 0 ? (
                <tr><td colSpan={6} className="rh-empty">No payments received yet.</td></tr>
              ) : history.map((p) => (
                <tr key={p.id}>
                  <td className="rh-text-sm rh-text-muted">{p.paid_at}</td>
                  <td className="rh-text-sm">{periodLabel(p.period)}</td>
                  <td>{p.comments_count}</td>
                  <td style={{ color: '#16a34a', fontWeight: 600 }}>${parseFloat(p.amount).toFixed(2)}</td>
                  <td className="rh-text-sm rh-text-muted">{METHOD_LABELS[p.method] || p.method}</td>
                  <td className="rh-text-sm rh-text-muted">{p.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MyAccountsView({ onBack }) {
  const [accounts, setAccounts] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const qc = useAccountQualityCheck();

  const load = useCallback(() => {
    redditApi.get('/my/accounts/')
      .then(({ data }) => setAccounts(data))
      .catch(() => setAccounts([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startCheck() {
    const username = accountSearch.trim().replace(/^u\//i, '');
    if (!username) return;
    setError('');
    qc.validate(username);
  }

  async function handleAddValidated() {
    if (!qc.result || qc.result.status !== 'passed') return;
    setAdding(true);
    setError('');
    try {
      await redditApi.post('/accounts/', { validation_id: qc.result.validation_id });
      setAccountSearch('');
      qc.reset();
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add account. Try again.');
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(account) {
    const linked = (account.comment_count || 0) > 0;
    if (linked) {
      setError(
        `u/${account.username} has ${account.comment_count} linked comment${account.comment_count !== 1 ? 's' : ''} and cannot be removed. Ask an admin to archive it.`,
      );
      return;
    }
    if (!window.confirm(`Remove u/${account.username}? This cannot be undone.`)) return;
    setDeletingId(account.id);
    setError('');
    try {
      await redditApi.delete(`/my/accounts/${account.id}/`);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove account. Try again.');
    } finally {
      setDeletingId(null);
    }
  }

  const STATUS_LABELS = {
    passed: 'Passed',
    failed: 'Failed',
    checking: 'Checking',
    needs_manual_review: 'Needs Review',
    error: 'Error',
    banned: 'Banned',
  };

  return (
    <div className="rh-redditor-body">
      <button
        onClick={onBack}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--rh-text-muted)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16, padding: 0 }}
      >
        ← Back to Dashboard
      </button>
      <h2 className="rh-page-title" style={{ marginBottom: 4 }}>My Accounts</h2>
      <p className="rh-page-subtitle" style={{ marginBottom: 20 }}>
        Add and quality-check your Reddit accounts ahead of time. Only accepted accounts can be used when marking comments as submitted.
      </p>

      {error && <p className="rh-input-error" style={{ marginBottom: 12 }}>{error}</p>}

      {/* Existing accounts */}
      <div className="rh-card" style={{ marginBottom: 20 }}>
        <div className="rh-card-header">
          <div>
            <h3 className="rh-card-title">Your Accounts</h3>
            <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', marginTop: 2 }}>
              Only <strong>Passed</strong> accounts can be used to submit comments. Accounts marked{' '}
              <strong>Failed</strong> are blocked from future submissions. Accounts with linked comments can't be removed.
            </p>
          </div>
        </div>
        <div className="rh-table-wrap">
          {accounts === null ? (
            <div className="rh-empty" style={{ padding: 20 }}>Loading…</div>
          ) : accounts.length === 0 ? (
            <div className="rh-empty" style={{ padding: 20 }}>No accounts yet. Add one below.</div>
          ) : (
            <table className="rh-table">
              <thead>
                <tr><th>Account</th><th>Status</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>u/{a.username}</td>
                    <td>
                      {a.force_added ? (
                        <span className="rh-badge rh-badge-force_added">Force Added</span>
                      ) : (
                        <span className={`rh-badge rh-badge-${a.validation_status}`}>
                          {STATUS_LABELS[a.validation_status] || a.validation_status}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => handleDelete(a)}
                        disabled={deletingId === a.id || (a.comment_count || 0) > 0}
                        title={(a.comment_count || 0) > 0
                          ? 'Linked to comments — ask an admin to archive instead'
                          : 'Remove account'}
                        style={{
                          background: 'none', border: 'none',
                          cursor: deletingId === a.id || (a.comment_count || 0) > 0 ? 'not-allowed' : 'pointer',
                          color: (a.comment_count || 0) > 0 ? '#d1d5db' : 'var(--rh-text-muted)',
                          padding: 6, lineHeight: 0, borderRadius: 6,
                          opacity: (a.comment_count || 0) > 0 ? 0.5 : 1,
                        }}
                      >
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add account */}
      <div className="rh-card">
        <div className="rh-card-header">
          <div>
            <h3 className="rh-card-title">Add an Account</h3>
            <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', marginTop: 2 }}>
              Enter a Reddit username and run a quality check. Accounts that pass can be added.
            </p>
          </div>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: qc.result || qc.phase === 'checking' ? 16 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, border: '1px solid var(--rh-border)', borderRadius: 8, paddingLeft: 10, background: '#fff' }}>
              <span style={{ color: 'var(--rh-text-muted)', fontFamily: 'monospace', fontSize: 13 }}>u/</span>
              <input
                type="text"
                placeholder="username"
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), startCheck())}
                disabled={qc.phase === 'checking'}
                style={{ flex: 1, border: 'none', outline: 'none', padding: '9px 8px', fontSize: 13, fontFamily: 'monospace', background: 'transparent' }}
              />
            </div>
            <button
              type="button"
              className="rh-btn-primary"
              onClick={startCheck}
              disabled={qc.phase === 'checking' || !accountSearch.trim().replace(/^u\//i, '')}
            >
              {qc.phase === 'checking' ? 'Checking…' : 'Check'}
            </button>
          </div>

          {(qc.phase === 'checking' || qc.result) && (
            <AccountQualityResult
              result={qc.result}
              adding={adding}
              onAddAccount={handleAddValidated}
              onRetry={qc.reset}
              onRerun={startCheck}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Skip Modal ──────────────────────────────────────────────────── */
const SKIP_REASONS = [
  { value: 'bot_bouncer',        label: 'Bot bouncer present' },
  { value: 'duplicate',          label: 'Previously posted by someone else/Duplicate comment' },
  { value: 'old_thread',         label: 'Old thread' },
  { value: 'comments_filtered',  label: 'Comments being filtered' },
  { value: 'unfit_brand',        label: 'Post is unfit for a brand mention' },
  { value: 'brand_promo_blocked', label: 'Brand promotion not allowed' },
  { value: 'mod_active_review',  label: 'Moderator is active and manually reviewing comments' },
  { value: 'other',             label: 'Other' },
];

function SkipModal({ comment, onClose, onSkipped }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState(comment.redditor_note || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onConfirm() {
    setError('');
    if (!reason) {
      setError('Please select a reason for skipping.');
      return;
    }
    const trimmed = note.trim();
    if (reason === 'other' && !trimmed) {
      setError('Please explain why you are skipping this comment (required for "Other").');
      return;
    }
    if (trimmed.length > 1000) {
      setError('Remarks must be 1000 characters or fewer.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await redditApi.post(`/my/comments/${comment.id}/skip/`, {
        skip_reason: reason,
        redditor_note: trimmed,
      });
      onSkipped(data);
      onClose();
    } catch (err) {
      const d = err.response?.data;
      setError(d?.redditor_note?.[0] || d?.skip_reason?.[0] || d?.detail || 'Failed to skip comment. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <h3 className="rh-modal-title" style={{ margin: 0 }}>Skip this comment</h3>
          <button className="rh-modal-close" onClick={onClose} type="button">×</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
          Use this when you cannot post this comment. A reason is required. The item is marked Skipped and removed from your To Do list — an admin will review it before any replacement is scheduled.
        </p>

        <div className="rh-modal-preview" style={{ marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)', marginBottom: 4 }}>
            {comment.client_name}
            {comment.subreddit ? ` · ${comment.subreddit.startsWith('r/') ? comment.subreddit : `r/${comment.subreddit}`}` : ''}
          </p>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>
            {comment.post_title || '—'}
          </p>
        </div>

        <div className="rh-form-group" style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Reason</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {SKIP_REASONS.map((r) => (
              <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="skip-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => { setReason(r.value); setError(''); }}
                  style={{ accentColor: 'var(--rh-orange)' }}
                />
                {r.label}
              </label>
            ))}
          </div>
        </div>

        <div className="rh-form-group">
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>
              Remarks
              {reason === 'other' && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
            </span>
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--rh-text-muted)' }}>
              {reason === 'other'
                ? 'Required — explain why you are skipping this comment.'
                : 'Optional extra context for the admin team.'}
            </span>
          </label>
          <textarea
            placeholder={reason === 'other' ? 'Explain why you are skipping this comment' : 'Optional context for the admin team'}
            value={note}
            onChange={(e) => { setNote(e.target.value); setError(''); }}
            rows={4}
            style={{
              width: '100%', resize: 'vertical', borderRadius: 8, padding: '8px 10px',
              border: `1.5px solid ${error ? '#dc2626' : 'var(--rh-border)'}`,
              fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box',
              outline: 'none', lineHeight: 1.5, background: '#fff',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--rh-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,69,0,0.12)'; }}
            onBlur={(e) => { e.target.style.borderColor = error ? '#dc2626' : 'var(--rh-border)'; e.target.style.boxShadow = 'none'; }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            {error
              ? <p className="rh-input-error" style={{ margin: 0 }}>{error}</p>
              : <span />}
            <span style={{ fontSize: 11, color: note.trim().length > 1000 ? '#dc2626' : 'var(--rh-text-faint)' }}>
              {note.trim().length}/1000
            </span>
          </div>
        </div>

        <div className="rh-modal-actions">
          <button className="rh-btn-secondary" onClick={onClose} disabled={busy} type="button">Cancel</button>
          <button className="rh-btn-primary" onClick={onConfirm} disabled={busy} type="button">
            {busy ? 'Skipping…' : 'Skip comment'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RedditorDashboard() {
  const { user, logout } = useRedditAuth();
  const [view, setView] = useState('assignments'); // 'assignments' | 'payments' | 'accounts'
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingComment, setViewingComment] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(null);
  const [skipComment, setSkipComment] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const menuRef = useRef(null);

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await redditApi.get('/my/comments/?tab=todo');
      setComments(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await redditApi.get('/my/stats/');
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadPayments = useCallback(async () => {
    try {
      const { data } = await redditApi.get('/my/payments/');
      setPayments(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadComments();
    loadStats();
    loadPayments();
  }, [loadComments, loadStats, loadPayments]);

  // Background poll every 15 s — keeps stats + payment KPIs live without a spinner
  useEffect(() => {
    const id = setInterval(() => {
      loadStats();
      loadPayments();
      redditApi.get('/my/comments/?tab=todo')
        .then(({ data }) => setComments(data))
        .catch(() => {});
    }, 15_000);
    return () => clearInterval(id);
  }, [loadStats, loadPayments]);

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const overdueComments = comments.filter((c) => isOverdueComment(c, today));
  const restComments = comments.filter((c) => !isOverdueComment(c, today));
  const overdueCount = overdueComments.length;
  const overduePerDay = overdueCount > 0 ? Math.max(1, Math.ceil(overdueCount / 10)) : 0;

  function onSubmitted(isDws = false) {
    setToast({ message: isDws ? '✓ Recorded as Instant Deletion!' : '✓ Comment marked as submitted!', type: 'success' });
    loadComments();
    loadStats();
    loadPayments();
  }

  function copyComment(text) {
    navigator.clipboard.writeText(text).then(() => {
      setToast({ message: 'Comment copied to clipboard', type: 'success' });
    });
  }

  const initials = (user?.full_name || '?').charAt(0).toUpperCase();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--rh-bg)', fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}>
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}

      {/* Top bar */}
      <header className="rh-topbar">
        <div className="rh-topbar-brand">
          <div className="rh-brand-icon" style={{ width: 34, height: 34, fontSize: 16 }}>R</div>
          <div>
            <span className="rh-topbar-title">Reddit Campaign Hub</span>
            <span className="rh-topbar-badge" style={{ marginLeft: 8 }}>Commenter</span>
          </div>
        </div>
        <div className="rh-topbar-user" ref={menuRef}>
          <button className="rh-topbar-user-btn" onClick={() => setMenuOpen((v) => !v)}>
            <div className="rh-user-avatar" style={{ width: 28, height: 28, fontSize: 12 }}>{initials}</div>
            <div style={{ textAlign: 'right' }}>
              <div className="rh-topbar-user-name">{user?.full_name}</div>
            </div>
            <span className="rh-topbar-user-caret">▼</span>
          </button>
          {menuOpen && (
            <div className="rh-dropdown">
              <button onClick={() => { setView('accounts'); setMenuOpen(false); }}>My Accounts</button>
              <button onClick={() => { setView('payments'); setMenuOpen(false); }}>My Payments</button>
              <button onClick={logout}>Sign out</button>
            </div>
          )}
        </div>
      </header>

      {view === 'accounts' ? (
        <MyAccountsView onBack={() => setView('assignments')} />
      ) : view === 'payments' ? (
        <MyPaymentsView onBack={() => setView('assignments')} data={payments} loading={payments === null} />
      ) : (
      <div className="rh-redditor-body">
        <div className="rh-redditor-dash-top">
        {/* Activity stats */}
        <div className="rh-kpi-row rh-kpi-row-4">
          <div className="rh-kpi-card rh-kpi-card--default">
            <div className="rh-kpi-body">
              <p className="rh-kpi-value default">{stats?.total ?? '—'}</p>
              <p className="rh-kpi-label">Total Assigned</p>
            </div>
            <div className="rh-kpi-icon-wrap rh-kpi-icon--default">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
          </div>
          <div className="rh-kpi-card rh-kpi-card--yellow">
            <div className="rh-kpi-body">
              <p className="rh-kpi-value yellow">{stats?.scheduled ?? '—'}</p>
              <p className="rh-kpi-label">Scheduled</p>
              {(stats?.overdue || 0) > 0 && (
                <p className="rh-kpi-hint">{stats.overdue} overdue. Post these first</p>
              )}
            </div>
            <div className="rh-kpi-icon-wrap rh-kpi-icon--yellow">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 1 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/>
              </svg>
            </div>
          </div>
          <div className="rh-kpi-card rh-kpi-card--green">
            <div className="rh-kpi-body">
              <p className="rh-kpi-value green">{stats?.submitted ?? '—'}</p>
              <p className="rh-kpi-label">Submitted</p>
            </div>
            <div className="rh-kpi-icon-wrap rh-kpi-icon--green">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
          </div>
          <div className="rh-kpi-card rh-kpi-card--red">
            <div className="rh-kpi-body">
              <p className="rh-kpi-value red">{stats ? `${stats.deletion_rate}%` : '—'}</p>
              <p className="rh-kpi-label">Deletion Rate</p>
            </div>
            <div className="rh-kpi-icon-wrap rh-kpi-icon--red">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Earnings row */}
        <div className="rh-kpi-row rh-kpi-row-3">
          <div className="rh-kpi-card rh-kpi-card--default">
            <div className="rh-kpi-body">
              <p className="rh-kpi-value default">{payments ? `$${payments.kpis.total_earned.toFixed(2)}` : '—'}</p>
              <p className="rh-kpi-label">Total Earned</p>
              {payments?.rate_per_comment > 0 && (
                <p className="rh-kpi-hint">${parseFloat(payments.rate_per_comment).toFixed(2)} per comment · paid monthly</p>
              )}
            </div>
            <div className="rh-kpi-icon-wrap rh-kpi-icon--default">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
          </div>
          <div className="rh-kpi-card rh-kpi-card--green">
            <div className="rh-kpi-body">
              <p className="rh-kpi-value green">{payments ? `$${payments.kpis.total_paid.toFixed(2)}` : '—'}</p>
              <p className="rh-kpi-label">Paid Out</p>
            </div>
            <div className="rh-kpi-icon-wrap rh-kpi-icon--green">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
          </div>
          <div className="rh-kpi-card rh-kpi-card--orange">
            <div className="rh-kpi-body">
              <p className="rh-kpi-value orange">
                {payments ? `$${(payments.kpis.due_now + payments.kpis.current_month).toFixed(2)}` : '—'}
              </p>
              <p className="rh-kpi-label">Pending Payment</p>
              <p className="rh-kpi-hint">
                {payments?.kpis.due_now > 0
                  ? `$${payments.kpis.due_now.toFixed(2)} due · $${payments.kpis.current_month.toFixed(2)} accruing`
                  : 'accruing this month'}
              </p>
            </div>
            <div className="rh-kpi-icon-wrap rh-kpi-icon--orange">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="rh-notice-board">
          <div className="rh-notice-board-head">
            <span>Notifications</span>
            {overdueCount > 0 && (
              <span className="rh-notice-board-count">{overdueCount} overdue</span>
            )}
          </div>
          {overdueCount > 0 && (
            <div className="rh-notice rh-notice--urgent">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div>
                <p className="rh-notice-title">
                  {overdueCount} overdue comment{overdueCount !== 1 ? 's' : ''}. Post these first
                </p>
                <p className="rh-notice-copy">
                  Please clear {overdueCount !== 1 ? 'them' : 'it'} within 10 working days (2 weeks)
                  {overdueCount > 1 ? `, about ${overduePerDay} per working day` : ''}.
                  Finish the oldest overdue items before this month's remaining comments.
                </p>
              </div>
            </div>
          )}
          <div className="rh-notice rh-notice--info">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <p className="rh-notice-copy">
              Commenting with a new Reddit account? Register and quality-check it in{' '}
              <button type="button" className="rh-notice-link" onClick={() => setView('accounts')}>
                My Accounts
              </button>{' '}
              <strong>before</strong> you post. Comments from unregistered accounts can't be recorded.
            </p>
          </div>
        </div>
        </div>

        {/* Pending comments — overdue first, then scheduled */}
        <div className="rh-card">
          <div className="rh-table-wrap rh-redditor-assignments">
            <table className="rh-table">
              <colgroup>
                <col style={{ width: 200 }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: 128 }} />
                <col />
                <col style={{ width: 108 }} />
                <col style={{ width: 128 }} />
                <col style={{ width: 158 }} />
                <col style={{ width: 176 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Post Title</th>
                  <th>Thread Created</th>
                  <th>Comment</th>
                  <th>Status</th>
                  <th>Publish Date</th>
                  <th>Recommended account</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="rh-empty">Loading…</td></tr>
                ) : comments.length === 0 ? (
                  <tr><td colSpan={8} className="rh-empty">No pending comments.</td></tr>
                ) : (
                  <>
                    {overdueCount > 0 && (
                      <tr className="rh-todo-group-row">
                        <td colSpan={8}>Overdue. Post these first</td>
                      </tr>
                    )}
                    {overdueComments.map((c) => (
                      <TodoAssignmentRow
                        key={c.id}
                        comment={c}
                        today={today}
                        hasOverdues={overdueCount > 0}
                        onView={setViewingComment}
                        onCopy={copyComment}
                        onSubmit={setSubmittingComment}
                        onSkip={setSkipComment}
                      />
                    ))}
                    {overdueCount > 0 && restComments.length > 0 && (
                      <tr className="rh-todo-group-row rh-todo-group-row--rest">
                        <td colSpan={8}>This month. After overdues</td>
                      </tr>
                    )}
                    {restComments.map((c) => (
                      <TodoAssignmentRow
                        key={c.id}
                        comment={c}
                        today={today}
                        hasOverdues={overdueCount > 0}
                        onView={setViewingComment}
                        onCopy={copyComment}
                        onSubmit={setSubmittingComment}
                        onSkip={setSkipComment}
                      />
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {viewingComment && (
        <ViewCommentModal comment={viewingComment} onClose={() => setViewingComment(null)} />
      )}
      {submittingComment && (
        <SubmitModal
          comment={submittingComment}
          onClose={() => setSubmittingComment(null)}
          onSubmitted={onSubmitted}
          onManageAccounts={() => { setSubmittingComment(null); setView('accounts'); }}
        />
      )}
      {skipComment && (
        <SkipModal
          comment={skipComment}
          onClose={() => setSkipComment(null)}
          onSkipped={(updated) => {
            setComments((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
            setToast({ message: '✓ Comment skipped.', type: 'success' });
          }}
        />
      )}
    </div>
  );
}
